import { MessageType, Notification, PageLoader } from '@innovationnorway/component-library';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CustomerInfoDto } from 'src/api/v2';
import Table from 'src/components/Table';
import { useExternalAccountingData } from 'src/hooks/use-external-accounting-data';
import { formatOrganizationNumber } from 'src/utils/format';
import CompanyEnterpriseStatusTag from './CompanyEnterpriseStatusTag';

type Props = {
  company: CustomerInfoDto;
};

const CompanyInformationEconomyTab = ({ company }: Props) => {
  const { t: tSiva } = useTranslation('siva');

  const { data, isLoading } = useExternalAccountingData(company.publicId);

  const accountingDatasets = useMemo(() => {
    if (data) {
      return (
        data
          // Filter out group accounting for now, may include these later
          .filter((item) => !item.groupAccounting?.includes('K'))
          .sort((a, b) => b.fiscalYear - a.fiscalYear)
          .slice(0, 3)
      );
    }

    return [];
  }, [data]);

  function formatNOK(value: number) {
    // values are in thousands
    const result = value * 1000;

    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
    })
      .format(result)
      .replace('kr', 'NOK');
  }

  const tableData = useMemo(() => {
    return accountingDatasets.map((item) => {
      const data = {
        salesIncome: 0, // omsetning
        balance: 0, // balanse
        valueCreation: 0, // verdiskapning
        operationalResults: 0, // driftsresultat
        salaryExpenses: 0, // lonnskostnader
      };

      if (item) {
        data.salesIncome = item.salesIncome || 0;
        data.operationalResults = item.operationalResults || 0;
        data.salaryExpenses = item.salaryExpenses || 0;
        data.valueCreation = data.operationalResults + data.salaryExpenses;

        data.balance = item.debtAndEquity || 0; // TODO: Finne ut hvordan vi regner ut denne
      }
      return {
        year: item.fiscalYear,
        data: [
          {
            key: 'salesIncome',
            value: formatNOK(data.salesIncome),
          },
          {
            key: 'balance',
            value: formatNOK(data.balance),
          },
          {
            key: 'valueCreation',
            value: formatNOK(data.valueCreation),
          },
          {
            key: 'operationalResults',
            value: formatNOK(data.operationalResults),
          },
          {
            key: 'salaryExpenses',
            value: formatNOK(data.salaryExpenses),
          },
          {
            key: 'enterpriseStatus',
            value: <CompanyEnterpriseStatusTag orgNo={company.publicId} />,
          },
        ] satisfies { key: string; value: React.ReactNode; className?: string }[],
      };
    });
  }, [company.publicId, accountingDatasets]);

  const categories = useMemo(() => {
    return [
      {
        key: 'salesIncome',
        label: tSiva('companyTabs.economy.salesIncome'),
      },
      {
        key: 'balance',
        label: tSiva('companyTabs.economy.balance'),
      },
      {
        key: 'valueCreation',
        label: tSiva('companyTabs.economy.valueCreation'),
      },
      {
        key: 'operationalResults',
        label: tSiva('companyTabs.economy.operationalResults'),
        className: 'padding-left--2',
      },
      {
        key: 'salaryExpenses',
        label: tSiva('companyTabs.economy.salaryExpenses'),
        className: 'padding-left--2',
      },
      {
        key: 'enterpriseStatus',
        label: tSiva('companyTabs.economy.enterpriseStatus'),
      },
    ];
  }, [tSiva]);

  if (isLoading) {
    return (
      <>
        <h3>{tSiva('companyTabs.economy.loading.text')}</h3>
        <PageLoader aria-label={tSiva('companyTabs.economy.loading.text')} />
      </>
    );
  }

  return (
    <>
      {accountingDatasets.length > 0 ? (
        <h3>{tSiva('companyTabs.tabs.economy')}</h3>
      ) : (
        <Notification type={MessageType.Info} fullWidth className="margin-bottom--4">
          <p>
            <Trans
              t={tSiva}
              i18nKey={'companyTabs.economy.notification.text1'}
              values={{ name: company.name, orgNo: formatOrganizationNumber(company.publicId) }}
              components={{
                '1': <strong>{company.name}</strong>,
              }}
            >
              Kunne ikke finne regnskaps data for <strong>{company.name}</strong> (
              {formatOrganizationNumber(company.publicId)}).
            </Trans>
          </p>

          <p>{tSiva('companyTabs.economy.notification.text2')}</p>
        </Notification>
      )}
      <Table>
        <Table.Body>
          <Table.Row>
            <Table.Cell />
            {tableData.map((items, index) => (
              <Table.Cell key={index} className="width--256px max-width--256px">
                <strong>{items.year}</strong>
              </Table.Cell>
            ))}
          </Table.Row>
          {categories.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell className="width--256px max-width--256px">
                <strong className={item?.className}>{item.label}</strong>
              </Table.Cell>
              {tableData.map((items, index) => {
                const data = items.data.find((dataItem) => dataItem.key === item.key);
                return <Table.Cell key={index}>{data ? data.value : ''}</Table.Cell>;
              })}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

export default CompanyInformationEconomyTab;
